import { useState } from 'react'

import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'

import { downloadFile } from '@/utils/downloadFile'

interface UseFileDownloadResultIProps {
  isDownloading: boolean
  handleDownload: (url: string, fileName: string) => Promise<void>
}

/**
 * Downloads a stored asset. Shared by every surface that renders a file the app
 * cannot display inline (incident images, note media, quick report answers).
 */
export const useFileDownload = (): UseFileDownloadResultIProps => {
  const [isDownloading, setIsDownloading] = useState(false)
  const { showError } = useToasts()

  const handleDownload = async (url: string, fileName: string) => {
    setIsDownloading(true)
    try {
      await downloadFile(url, fileName)
    } catch (error) {
      Sentry.captureException(error, {
        tags: { action: 'download', feature: 'stored_assets' },
        extra: { fileName },
      })
      showError(`Failed to download ${fileName}`)
    } finally {
      setIsDownloading(false)
    }
  }

  return { isDownloading, handleDownload }
}
